/**
 * Utility functions for filtering, sorting, and counting work queue issues
 */

import type { IssueSummary, IssueTagType } from '../../../domain'
import type { TagCounts, WorkQueueSortBy, WorkQueueSortDirection } from '../types/workQueueFilters'
import { compareSeverity, compareDates } from '../../../utils/sortUtils'

/**
 * Compute counts for each tag type from a list of issues
 */
export function computeTagCounts(issues: IssueSummary[]): TagCounts {
  const counts: Partial<TagCounts> = {}

  for (const issue of issues) {
    for (const tag of issue.tags) {
      counts[tag.type] = (counts[tag.type] ?? 0) + 1
    }
  }

  return counts as TagCounts
}

/**
 * Sort issues by the specified field and direction
 */
export function sortIssues(
  issues: IssueSummary[],
  sortBy: WorkQueueSortBy,
  direction: WorkQueueSortDirection
): IssueSummary[] {
  const sorted = [...issues]
  const multiplier = direction === 'asc' ? 1 : -1

  sorted.sort((a, b) => {
    switch (sortBy) {
      case 'severity':
        return compareSeverity(a.severity, b.severity) * multiplier
      case 'createdAt':
      default:
        return compareDates(a.createdAt, b.createdAt) * multiplier
    }
  })

  return sorted
}

/**
 * Filter issues by search query (case-insensitive title match)
 */
export function filterIssuesBySearch(issues: IssueSummary[], query: string): IssueSummary[] {
  const normalizedQuery = query.trim().toLowerCase()
  if (!normalizedQuery) {
    return issues
  }

  return issues.filter((issue) => issue.title.toLowerCase().includes(normalizedQuery))
}

/**
 * Filter issues by tag type
 */
export function filterIssuesByTag(issues: IssueSummary[], tagType: IssueTagType | null): IssueSummary[] {
  if (!tagType) {
    return issues
  }

  return issues.filter((issue) => issue.tags.some((tag) => tag.type === tagType))
}
